import {
  AccountActionListTile,
  AccountActionTile,
  CardTrackerAttachmentTile,
  CardTrackerAttachmentTileType,
  ITile,
  ListTile,
  TileStatus,
  TileType,
  Typenames,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import {
  isApplePayRoute,
  isGooglePayRoute,
} from 'features/moveMoney/helpers/MoveMoneyTile.helpers';
import { AccountActionCardActivationProps } from 'shared/components/vxl/cards/HorizontalAccountCard/AccountActionList/AccountActionCardActivation/AccountActionCardActivation';
import { AccountActionProps } from 'shared/components/vxl/cards/HorizontalAccountCard/AccountActionList/AccountActionItem/AccountActionItem';
import { AccountActionsContainerProps } from 'shared/components/vxl/cards/HorizontalAccountCard/AccountActionList/AccountActionList';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import { TagProps } from 'shared/components/vxl/Tag/Tag';
import { toTagIconKey, toTagType } from 'shared/components/vxl/Tag/Tag.helpers';
import { Maybe } from 'shared/utils/types';

export const mapInternalAccountChildTiles = (
  tiles: ITile[],
  {
    navigateToRoute,
    translatedText,
    isNewSparkleTagEnabled,
  }: HomeCardDependencies,
): Maybe<AccountActionsContainerProps> => {
  // Different accounts hold their account actions differently:
  // Believe uses a type called `AccountActionListTile` and Checking uses `ListTile`
  // pick one or the other from the tiles under the InternalAccountTile.
  const listTile = tiles.find((tile) => {
    if (
      '__typename' in tile &&
      (tile.__typename === Typenames.ListTile ||
        tile.__typename === Typenames.AccountActionListTile) &&
      tile.status === TileStatus.Loaded
    ) {
      return tile;
    }
    return null;
  }) as Maybe<ListTile & AccountActionListTile>;

  const { title, listTitle, tiles: accountActionTiles } = listTile ?? {};
  // card tracker tile may also be present, if found append to end of actions
  const cardTrackerTile: CardTrackerAttachmentTile = tiles.find((tile) => {
    if (
      tile.tileType === TileType.CardTrackerAttachment &&
      tile.status === TileStatus.Loaded
    ) {
      return tile as CardTrackerAttachmentTile;
    }
    return null;
  }) as CardTrackerAttachmentTile;

  // Narrow any ITile to a loaded AccountActionTile that includes a CTA
  const isLoadedAccountActionTile = (t: ITile): t is AccountActionTile => {
    return (
      t.tileType === TileType.AccountAction &&
      t.status === TileStatus.Loaded &&
      'cta' in t
    );
  };

  // since all tiles under the list tile should be a AccountAction we will filter
  // for that and cast it here as well exiting early if no actions are given.
  const accountActions: (
    | AccountActionProps
    | AccountActionCardActivationProps
  )[] = (accountActionTiles ?? [])
    .flatMap((tile) => {
      if (!isLoadedAccountActionTile(tile)) {
        return null;
      }

      if (tile.tileType === TileType.AccountAction) {
        const isApplePayTile = isApplePayRoute(tile.onClickV2?.route);
        const isGooglePayTile = isGooglePayRoute(tile.onClickV2?.route);
        const shouldShowTag =
          isApplePayTile || isGooglePayTile ? isNewSparkleTagEnabled : true;
        // `tag` is a purely decorative badge (e.g. "New" sparkle label)
        // displayed on the tile. `onPress` is the tile-level navigation
        // handler and is always set regardless of tag visibility.
        // The `onClickV2` null-check inside `onPress` is a defensive guard;
        // the GQL schema guarantees `onClickV2` is always present, so in
        // practice the condition is always true. If it were ever absent, the
        // press would be a safe no-op.
        const props: AccountActionProps = {
          id: tile.id,
          titleText: tile.cta,
          illustrationKey: tile.iconV2 as IllustrationKeyType,
          tag: shouldShowTag
            ? generateTagsFromAccountActionTile(tile)
            : undefined,
          onPress: () => {
            if (tile.onClickV2) {
              navigateToRoute(tile.onClickV2);
            }
          },
        };
        return props;
      }
      return null;
    })
    .filter(
      (item): item is AccountActionProps | AccountActionCardActivationProps =>
        item !== null,
    );

  if (cardTrackerTile) {
    const cardShippingMessage = cardTrackerTile.card?.shippingInformationV2
      ?.arrivalMessage
      ? cardTrackerTile.card.shippingInformationV2.arrivalMessage
      : null;

    const { route } = cardTrackerTile;
    const cardTrackerProps: AccountActionCardActivationProps = {
      id: cardTrackerTile.id,
      titleText: cardTrackerTile.title,
      illustrationKey:
        cardTrackerTile.cardTrackerAttachmentTileType ===
        CardTrackerAttachmentTileType.DebitCard
          ? 'DEBIT_CARD_MAS'
          : 'BELIEVE_MAS',
      cardActivationAction: () => {
        if (route) {
          navigateToRoute(route);
        }
      },
      infoText: cardShippingMessage ?? undefined,
    };

    accountActions.push(cardTrackerProps);
  }

  if (!accountActions.length) {
    return null;
  }
  const cardListTitle =
    !listTitle && cardTrackerTile
      ? translatedText('checkingAccountTile.title')
      : '';

  return {
    titleText: title ?? listTitle ?? cardListTitle ?? '',
    actions: accountActions,
  };
};

function generateTagsFromAccountActionTile(
  tile: AccountActionTile,
): TagProps | undefined {
  if (tile.tags.length < 1) {
    return undefined;
  }
  // Only ever 1 tag will be used so we are to just grab it from the array
  const tagFragment = tile.tags[0];

  if (!tagFragment) {
    return undefined;
  }

  const iconKey = tagFragment.icon ? toTagIconKey(tagFragment.icon) : null;

  return {
    text: tagFragment.text,
    type: toTagType(tagFragment.tagColor),
    iconKey: iconKey ?? undefined,
  };
}
